import React, { useState, useEffect } from 'react';
import {
  User, Mail, Phone, Building, MapPin, Save, Camera, Bell, Shield,
  Globe, Moon, Volume2, Lock, Key, Eye, EyeOff,
  Calendar, Link, UserCheck, Plus, X, Copy, CreditCard, RefreshCw, LogOut, Fingerprint
} from 'lucide-react';
import CalendarSyncSettings from '../components/CalendarSyncSettings';
import PaymentSettings from '../components/PaymentSettings';
import NPILinkingView from './NPILinkingView';
import CacheManager from '../components/CacheManager';
import { authService } from '../utils/supabase';
import { logger } from '../utils/logger';
import { preApprovedRepsService } from '../utils/preApprovedRepsService';
import { biometricAuth } from '../utils/biometricAuth';
import { pushNotificationService } from '../utils/pushNotificationService';

const SettingsView = ({ userType, profileData, setProfileData, saveStatus, setSaveStatus, currentUser, setCurrentUser, onLogout }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    location: '',
    specialty: '',
    territory: '',
    npi_number: '',
    products: []
  });

  // State for approved reps (must be declared before useEffects)
  const [approvedReps, setApprovedReps] = useState([]);
  const [newRepEmail, setNewRepEmail] = useState('');
  const [loadingReps, setLoadingReps] = useState(false);
  const [preApprovedCustomers, setPreApprovedCustomers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchingReps, setSearchingReps] = useState(false);

  // Developer Tools visibility control
  // Only visible in development or for specific admin emails
  const SHOW_DEVELOPER_TOOLS = process.env.NODE_ENV === 'development' ||
                                process.env.REACT_APP_SHOW_DEV_TOOLS === 'true' ||
                                currentUser?.email === 'rclay@panaceavcc.com';

  // Load current user data on component mount
  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.first_name && currentUser.last_name ?
              `${currentUser.first_name} ${currentUser.last_name}` :
              currentUser.first_name || profileData?.name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        company: currentUser.company || '',
        title: currentUser.title || '',
        location: currentUser.location || '',
        specialty: currentUser.specialty || '',
        territory: currentUser.territory || '',
        npi_number: currentUser.npi_number || '',
        products: Array.isArray(currentUser.products)
          ? currentUser.products
          : (currentUser.products && typeof currentUser.products === 'string'
              ? currentUser.products.split(',')
              : [])
      });
    } else {
      // Empty data if no user
      setFormData({
        name: profileData?.name || '',
        email: '',
        phone: '',
        company: '',
        title: '',
        location: '',
        specialty: '',
        territory: '',
        npi_number: '',
        products: []
      });
    }
  }, [currentUser, profileData]);

  // Load pre-approved reps for customers
  useEffect(() => {
    const loadPreApprovedReps = async () => {
      if (currentUser && userType === 'customer') {
        try {
          setLoadingReps(true);
          const reps = await preApprovedRepsService.getPreApprovedReps(currentUser.id);
          setApprovedReps(reps.map(rep => ({
            id: rep.salesRepId,
            name: rep.salesRepName || 'Unknown',
            company: rep.salesRepCompany || 'Unknown Company',
            email: rep.salesRepEmail || '',
            approved: true,
            approvedAt: rep.approvedAt
          })));
        } catch (error) {
          logger.error('Error loading pre-approved reps:', error);
        } finally {
          setLoadingReps(false);
        }
      }
    };
    loadPreApprovedReps();
  }, [currentUser, userType]);

  // Load pre-approved customers for sales reps
  useEffect(() => {
    const loadPreApprovedCustomers = async () => {
      if (currentUser && (userType === 'sales' || userType === 'manager')) {
        try {
          setLoadingReps(true);
          const customers = await preApprovedRepsService.getCustomersWhoPreApproved(currentUser.id);
          setPreApprovedCustomers(customers.map(customer => ({
            id: customer.customerId,
            name: customer.customerName || 'Unknown',
            company: customer.customerCompany || 'Healthcare Provider',
            email: customer.customerEmail || '',
            approvedAt: customer.approvedAt
          })));
        } catch (error) {
          logger.error('Error loading pre-approved customers:', error);
        } finally {
          setLoadingReps(false);
        }
      }
    };
    loadPreApprovedCustomers();
  }, [currentUser, userType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearchDropdown) {
        const dropdown = document.querySelector('.search-dropdown-container');
        if (dropdown && !dropdown.contains(event.target)) {
          setShowSearchDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchDropdown]);

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    meetingReminders: true,
    complianceReports: userType === 'sales' ? true : false,
    marketingEmails: false,
    darkMode: false,
    language: 'en',
    timezone: 'America/Los_Angeles',
    soundEnabled: true
  });

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
    sessionTimeout: '30'
  });

  // Biometric authentication state
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometryType, setBiometryType] = useState('none');
  const [biometricLoading, setBiometricLoading] = useState(false);

  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const [calendarSettings, setCalendarSettings] = useState({
    enableCalendarSync: false,
    calendarUrl: '',
    syncFrequency: 'hourly',
    showAvailability: true
  });

  // Load existing profile picture
  useEffect(() => {
    if (currentUser && currentUser.profile_picture) {
      setProfilePicturePreview(currentUser.profile_picture);
    }
  }, [currentUser]);

  // Check biometric availability on mount
  useEffect(() => {
    const checkBiometric = async () => {
      try {
        const { isAvailable, biometryType: type } = await biometricAuth.isAvailable();
        setBiometricAvailable(isAvailable);
        setBiometryType(type);
        setBiometricEnabled(biometricAuth.isBiometricEnabled());
        logger.debug('Biometric availability in settings:', { isAvailable, type });
      } catch (error) {
        logger.error('Error checking biometric availability:', error);
      }
    };
    checkBiometric();
  }, []);

  // Check push notification availability on mount
  useEffect(() => {
    const checkPushNotifications = async () => {
      try {
        logger.debug('Checking push notification availability...');
        await pushNotificationService.initialize();
        const isSupported = pushNotificationService.isSupported();
        logger.debug('Push notification isSupported:', { isSupported });
        setPushSupported(isSupported);

        if (isSupported) {
          const enabled = await pushNotificationService.areNotificationsEnabled();
          logger.debug('Push notifications currently enabled:', { enabled });
          setPushEnabled(enabled);
        }
        logger.debug('Push notification availability check complete:', { isSupported, pushLoading });
      } catch (error) {
        logger.error('Error checking push notification availability:', error);
      }
    };
    checkPushNotifications();
  }, []);

  // Handle profile picture upload with compression
  const handleProfilePictureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      // Compress and convert to base64 for storage
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for compression
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set max dimensions (200x200 for profile pictures)
          const maxWidth = 200;
          const maxHeight = 200;
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Draw and compress image
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with compression (0.7 quality)
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);

          setProfilePicture(compressedBase64);
          setProfilePicturePreview(compressedBase64);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (section) => {
    if (section === 'profile' && currentUser) {
      try {
        const nameParts = formData.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        const updateData = {
          first_name: firstName,
          last_name: lastName,
          phone: formData.phone,
          company: formData.company,
          title: formData.title,
          location: formData.location,
          specialty: formData.specialty,
          territory: formData.territory,
          npi_number: formData.npi_number,
          products: formData.products.join(',')
        };

        // Add profile picture if it was updated
        if (profilePicture) {
          updateData.profile_picture = profilePicture;
        }

        const updatedProfile = await authService.updateUserProfile(currentUser.id, updateData);

        if (updatedProfile) {
          // Update currentUser with the new profile data including profile picture
          if (setCurrentUser) {
            setCurrentUser({ ...currentUser, ...updatedProfile });
          }

          // Also update the parent component's profile data if available
          setProfileData && setProfileData(formData);
          setSaveStatus && setSaveStatus('Profile saved successfully!');
        }
      } catch (error) {
        logger.error('Error saving profile:', error);
        setSaveStatus && setSaveStatus('Error saving profile. Please try again.');
      }
    } else {
      setSaveStatus && setSaveStatus('Settings saved successfully!');
    }
    setTimeout(() => setSaveStatus && setSaveStatus(''), 3000);
  };

  // Handle push notification toggle
  const handlePushNotificationToggle = async (enabled) => {
    logger.debug('Push toggle clicked', { enabled, currentUser: currentUser?.id, pushLoading });

    if (!currentUser) {
      logger.error('Push toggle: No current user');
      return;
    }

    setPushLoading(true);

    try {
      if (enabled) {
        const result = await pushNotificationService.requestPermission(currentUser.id);
        if (result.success) {
          setPushEnabled(true);
          setSaveStatus('Push notifications enabled!');
        } else {
          setSaveStatus(result.error || 'Failed to enable push notifications');
        }
      } else {
        const result = await pushNotificationService.disableNotifications(currentUser.id);
        if (result.success) {
          setPushEnabled(false);
          setSaveStatus('Push notifications disabled');
        } else {
          setSaveStatus(result.error || 'Failed to disable push notifications');
        }
      }
    } catch (error) {
      logger.error('Error toggling push notifications:', error);
      setSaveStatus('Error updating notification settings');
    } finally {
      setPushLoading(false);
      setTimeout(() => setSaveStatus && setSaveStatus(''), 3000);
    }
  };

  // Handle biometric toggle
  const handleBiometricToggle = async (enabled) => {
    if (!currentUser) return;

    setBiometricLoading(true);

    try {
      if (enabled) {
        // Need to prompt for password to store credentials
        const password = prompt('Enter your password to enable biometric login:');
        if (!password) {
          setBiometricLoading(false);
          return;
        }

        const result = await biometricAuth.enableBiometric(currentUser.email, password);
        if (result.success) {
          setBiometricEnabled(true);
          setSaveStatus(`${biometricAuth.getBiometryName(biometryType)} login enabled!`);
        } else {
          setSaveStatus(result.error || 'Failed to enable biometric login');
        }
      } else {
        const result = await biometricAuth.disableBiometric();
        if (result.success) {
          setBiometricEnabled(false);
          setSaveStatus('Biometric login disabled');
        } else {
          setSaveStatus(result.error || 'Failed to disable biometric login');
        }
      }
    } catch (error) {
      logger.error('Error toggling biometric:', error);
      setSaveStatus('Error updating biometric settings');
    } finally {
      setBiometricLoading(false);
      setTimeout(() => setSaveStatus && setSaveStatus(''), 3000);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <User className="w-4 h-4" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-4 h-4" /> },
    { id: 'security', label: 'Security', icon: <Shield className="w-4 h-4" /> },
    { id: 'preferences', label: 'Preferences', icon: <Globe className="w-4 h-4" /> },
    { id: 'calendar', label: userType === 'sales' ? 'Calendar Sync' : 'Calendar Integration', icon: <Calendar className="w-4 h-4" /> },
    ...(SHOW_DEVELOPER_TOOLS ? [
      { id: 'developer', label: 'Developer Tools', icon: <RefreshCw className="w-4 h-4" /> }
    ] : []),
    ...(userType === 'sales' || userType === 'manager' ? [
      { id: 'payment', label: 'Subscription', icon: <CreditCard className="w-4 h-4" /> },
      { id: 'preapproved', label: 'Pre-Approved By', icon: <UserCheck className="w-4 h-4" /> }
    ] : []),
    ...(userType === 'customer' ? [
      { id: 'npi', label: 'NPI Profile', icon: <Link className="w-4 h-4" /> },
      { id: 'representatives', label: 'Approved Reps', icon: <UserCheck className="w-4 h-4" /> }
    ] : [])
  ];

  const toggleRepApproval = async (repId) => {
    const rep = approvedReps.find(r => r.id === repId);
    if (!rep) return;

    try {
      if (rep.approved) {
        // Remove from Supabase
        await preApprovedRepsService.removePreApprovedRep(currentUser.id, repId);
        setApprovedReps(approvedReps.filter(r => r.id !== repId));
        setSaveStatus('Sales representative removed from pre-approved list');
      } else {
        // Add to Supabase
        await preApprovedRepsService.addPreApprovedRep(currentUser.id, repId);
        setApprovedReps(approvedReps.map(r =>
          r.id === repId ? { ...r, approved: true } : r
        ));
        setSaveStatus('Sales representative added to pre-approved list');
      }
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      logger.error('Error toggling rep approval:', error);
      setSaveStatus('Error updating pre-approval status');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Search for sales reps as user types
  const handleSearchReps = async (searchQuery) => {
    setNewRepEmail(searchQuery);

    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    try {
      setSearchingReps(true);
      const results = await preApprovedRepsService.searchSalesReps(searchQuery.trim());

      // Filter out reps that are already approved
      const approvedRepIds = new Set(approvedReps.map(rep => rep.id));
      const filteredResults = results.filter(rep => !approvedRepIds.has(rep.id));

      setSearchResults(filteredResults);
      setShowSearchDropdown(filteredResults.length > 0);
    } catch (error) {
      logger.error('Error searching reps:', error);
      setSearchResults([]);
      setShowSearchDropdown(false);
    } finally {
      setSearchingReps(false);
    }
  };

  // Add a rep by clicking on search result
  const addRepFromSearch = async (rep) => {
    if (!currentUser) return;

    try {
      setLoadingReps(true);
      setShowSearchDropdown(false);

      const result = await preApprovedRepsService.addPreApprovedRep(currentUser.id, rep.id);

      if (result) {
        // Reload the list
        const reps = await preApprovedRepsService.getPreApprovedReps(currentUser.id);
        setApprovedReps(reps.map(r => ({
          id: r.salesRepId,
          name: r.salesRepName || 'Unknown',
          company: r.salesRepCompany || 'Unknown Company',
          email: r.salesRepEmail || '',
          approved: true,
          approvedAt: r.approvedAt
        })));
        setNewRepEmail('');
        setSearchResults([]);
        setSaveStatus('Sales representative added successfully');
      } else {
        setSaveStatus('Error adding sales representative');
      }
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      logger.error('Error adding rep:', error);
      setSaveStatus('Error adding sales representative');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setLoadingReps(false);
    }
  };

  const addNewRep = async () => {
    if (!newRepEmail.trim() || !currentUser) return;

    try {
      setLoadingReps(true);
      setShowSearchDropdown(false);

      // Find the sales rep by email in Supabase
      const result = await preApprovedRepsService.addPreApprovedRepByEmail(
        currentUser.id,
        newRepEmail.trim()
      );

      if (result && result.salesRepId) {
        // Reload the list
        const reps = await preApprovedRepsService.getPreApprovedReps(currentUser.id);
        setApprovedReps(reps.map(rep => ({
          id: rep.salesRepId,
          name: rep.salesRepName || 'Unknown',
          company: rep.salesRepCompany || 'Unknown Company',
          email: rep.salesRepEmail || '',
          approved: true,
          approvedAt: rep.approvedAt
        })));
        setNewRepEmail('');
        setSearchResults([]);
        setSaveStatus('Sales representative added successfully');
      } else {
        setSaveStatus('Could not find sales rep with that email');
      }
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      logger.error('Error adding new rep:', error);
      setSaveStatus('Error adding sales representative');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setLoadingReps(false);
    }
  };

  const removeRep = async (repId) => {
    if (!currentUser) return;

    try {
      await preApprovedRepsService.removePreApprovedRep(currentUser.id, repId);
      setApprovedReps(approvedReps.filter(rep => rep.id !== repId));
      setSaveStatus('Sales representative removed from pre-approved list');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      logger.error('Error removing rep:', error);
      setSaveStatus('Error removing sales representative');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const generateCalendarUrl = () => {
    const baseUrl = window.location.origin;
    const userId = formData.email.replace('@', '_').replace('.', '_');
    return `${baseUrl}/calendar/${userId}/availability.ics`;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Settings</h2>
        <p className="text-gray-600 mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6 -mx-6 px-6 overflow-x-auto touch-pan-x">
        <nav className="flex space-x-8 min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-6 mb-6">
              <div className="relative">
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                  {profilePicturePreview ? (
                    <img
                      src={profilePicturePreview}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-12 h-12 text-gray-400" />
                  )}
                </div>
                <input
                  type="file"
                  id="profile-picture-upload"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  className="hidden"
                />
                <button
                  onClick={() => document.getElementById('profile-picture-upload').click()}
                  className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors"
                  title="Upload profile picture"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{formData.name}</h3>
                <p className="text-gray-600">{formData.title}</p>
                <p className="text-sm text-gray-500">{formData.company}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ paddingLeft: '44px' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ paddingLeft: '44px' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Professional Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder={userType === 'customer' ? 'e.g., Cardiologist, Nurse Practitioner' : 
                              userType === 'manager' ? 'e.g., Sales Manager, District Manager' : 
                              'e.g., Senior Sales Representative'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ paddingLeft: '44px' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {userType === 'customer' ? 'Hospital/Practice' : 'Company'}
                </label>
                <div className="relative">
                  <Building className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ paddingLeft: '44px' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {userType === 'customer' ? 'Location' : userType === 'manager' ? 'Management Region' : 'Territory'}
                </label>
                <div className="relative">
                  <MapPin className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
                  <input
                    type="text"
                    value={userType === 'customer' ? formData.location : formData.territory}
                    onChange={(e) => setFormData({
                      ...formData,
                      [userType === 'customer' ? 'location' : 'territory']: e.target.value
                    })}
                    className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ paddingLeft: '44px' }}
                  />
                </div>
              </div>

              {userType === 'customer' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Medical Specialty
                    </label>
                    <select
                      value={formData.specialty}
                      onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Cardiology">Cardiology</option>
                      <option value="Orthopedics">Orthopedics</option>
                      <option value="Neurology">Neurology</option>
                      <option value="Pediatrics">Pediatrics</option>
                      <option value="Internal Medicine">Internal Medicine</option>
                      <option value="Oncology">Oncology</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                      <span>NPI Number</span>
                      {formData.npi_number && (
                        <span className="flex items-center space-x-1 text-xs text-green-600">
                          <Link className="w-4 h-4" />
                          <span>Linked</span>
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={formData.npi_number}
                      onChange={(e) => setFormData({...formData, npi_number: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="1234567890"
                      maxLength="10"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Your 10-digit National Provider Identifier
                      {formData.npi_number && (
                        <span className="text-green-600 font-medium"> • Verified via NPI Registry</span>
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {saveStatus && (
                  <span className="text-green-600 text-sm flex items-center">
                    <Save className="w-4 h-4 mr-1" />
                    {saveStatus}
                  </span>
                )}
              </div>
              <button
                onClick={() => handleSave('profile')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          {/* Mobile Push Notifications - Only show on native app */}
          {pushSupported && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
                <Bell className="w-5 h-5 text-blue-600" />
                <span>Mobile Push Notifications</span>
              </h3>

              <div className="flex items-center justify-between py-3" style={{ position: 'relative', zIndex: 10 }}>
                <div>
                  <p className="font-medium text-gray-800">Enable Push Notifications</p>
                  <p className="text-sm text-gray-600">
                    Get notified when meetings are approved, cancelled, or rescheduled
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" style={{ zIndex: 20 }}>
                  <input
                    type="checkbox"
                    checked={pushEnabled}
                    onChange={(e) => handlePushNotificationToggle(e.target.checked)}
                    disabled={pushLoading}
                    style={{ position: 'absolute', width: '44px', height: '24px', opacity: 0, zIndex: 30, cursor: 'pointer' }}
                  />
                  <div className={`w-11 h-6 bg-gray-200 rounded-full peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${pushEnabled ? 'bg-blue-600' : 'bg-gray-200'} ${pushLoading ? 'opacity-50' : ''}`} style={{ position: 'relative' }}>
                    <span
                      className="absolute top-[2px] left-[2px] bg-white border border-gray-300 rounded-full h-5 w-5 transition-transform"
                      style={{ transform: pushEnabled ? 'translateX(20px)' : 'translateX(0)' }}
                    />
                  </div>
                </label>
              </div>

              {pushEnabled && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Push notifications are enabled. You'll receive alerts for meeting updates.
                  </p>
                </div>
              )}

              {!pushEnabled && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Enable push notifications to get instant alerts about your meetings.
                  </p>
                </div>
              )}

              <div className="mt-4 text-xs text-gray-500">
                <p>You'll receive notifications for:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>New meeting requests</li>
                  <li>Meeting confirmations</li>
                  <li>Meeting cancellations</li>
                  <li>Meeting reminders</li>
                </ul>
              </div>
            </div>
          )}

          {/* General Notification Preferences */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Notification Preferences</h3>

            <div className="space-y-6">
              {[
                { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive notifications via email' },
                { key: 'smsNotifications', label: 'SMS Notifications', desc: 'Receive text message notifications' },
                { key: 'meetingReminders', label: 'Meeting Reminders', desc: 'Get reminded about upcoming meetings' },
                ...(userType === 'sales' ? [{ key: 'complianceReports', label: 'Compliance Reports', desc: 'Monthly compliance report notifications' }] : []),
                { key: 'marketingEmails', label: 'Marketing Emails', desc: 'Receive product updates and newsletters' }
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-gray-800">{item.label}</p>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences[item.key]}
                      onChange={(e) => setPreferences({...preferences, [item.key]: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <button
                onClick={() => handleSave('notifications')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Preferences</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Password Security</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <Lock className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={security.currentPassword}
                    onChange={(e) => setSecurity({...security, currentPassword: e.target.value})}
                    className="w-full pr-12 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ paddingLeft: '44px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Key className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={security.newPassword}
                    onChange={(e) => setSecurity({...security, newPassword: e.target.value})}
                    className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ paddingLeft: '44px' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Key className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={security.confirmPassword}
                    onChange={(e) => setSecurity({...security, confirmPassword: e.target.value})}
                    className="w-full pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ paddingLeft: '44px' }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => handleSave('security')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Update Password</span>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Two-Factor Authentication</h3>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-800">Enable Two-Factor Authentication</p>
                <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={security.twoFactorEnabled}
                  onChange={(e) => setSecurity({...security, twoFactorEnabled: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Biometric Authentication - Only show on supported devices */}
          {biometricAvailable && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
                <Fingerprint className="w-5 h-5 text-blue-600" />
                <span>{biometricAuth.getBiometryName(biometryType)}</span>
              </h3>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-800">Enable {biometricAuth.getBiometryName(biometryType)}</p>
                  <p className="text-sm text-gray-600">
                    Sign in quickly using {biometryType === 'FACE_ID' ? 'facial recognition' : 'your fingerprint'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={biometricEnabled}
                    onChange={(e) => handleBiometricToggle(e.target.checked)}
                    disabled={biometricLoading}
                    className="sr-only peer"
                  />
                  <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${biometricLoading ? 'opacity-50' : ''}`}></div>
                </label>
              </div>

              {biometricEnabled && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    {biometricAuth.getBiometryName(biometryType)} is enabled. You can use it to sign in from the login screen.
                  </p>
                </div>
              )}

              {!biometricEnabled && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Enable {biometricAuth.getBiometryName(biometryType)} to sign in faster without entering your password each time.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Application Preferences</h3>
          
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Moon className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-800">Dark Mode</p>
                  <p className="text-sm text-gray-600">Switch to dark theme</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.darkMode}
                  onChange={(e) => setPreferences({...preferences, darkMode: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Volume2 className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-800">Sound Effects</p>
                  <p className="text-sm text-gray-600">Enable notification sounds</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.soundEnabled}
                  onChange={(e) => setPreferences({...preferences, soundEnabled: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  value={preferences.language}
                  onChange={(e) => setPreferences({...preferences, language: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={preferences.timezone}
                  onChange={(e) => setPreferences({...preferences, timezone: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => handleSave('preferences')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Preferences</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Integration Tab */}
      {activeTab === 'calendar' && userType === 'sales' && (
        <CalendarSyncSettings currentUser={currentUser} />
      )}

      {/* Calendar Integration Tab for Healthcare Providers */}
      {activeTab === 'calendar' && userType === 'customer' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Calendar Sync Settings</h3>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-800">Enable Calendar Synchronization</p>
                    <p className="text-sm text-gray-600">Sync your availability with external calendars</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={calendarSettings.enableCalendarSync}
                    onChange={(e) => setCalendarSettings({...calendarSettings, enableCalendarSync: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {calendarSettings.enableCalendarSync && (
                <div className="border-t pt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Calendar Feed URL
                    </label>
                    <div className="flex space-x-2">
                      <div className="flex-1 relative">
                        <Link className="absolute top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" style={{ left: '12px' }} />
                        <input
                          type="url"
                          value={generateCalendarUrl()}
                          readOnly
                          className="w-full pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none"
                          style={{ paddingLeft: '44px' }}
                        />
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(generateCalendarUrl())}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                      >
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Use this URL to subscribe to your availability calendar in Outlook, Google Calendar, or Apple Calendar
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sync Frequency
                    </label>
                    <select
                      value={calendarSettings.syncFrequency}
                      onChange={(e) => setCalendarSettings({...calendarSettings, syncFrequency: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="realtime">Real-time</option>
                      <option value="hourly">Every hour</option>
                      <option value="daily">Daily</option>
                      <option value="manual">Manual only</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800">Show Public Availability</p>
                      <p className="text-sm text-gray-600">Allow sales reps to see your general availability</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={calendarSettings.showAvailability}
                        onChange={(e) => setCalendarSettings({...calendarSettings, showAvailability: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6">
              <button
                onClick={() => handleSave('calendar')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Calendar Settings</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Settings Tab */}
      {activeTab === 'payment' && (userType === 'sales' || userType === 'manager') && (
        <PaymentSettings currentUser={currentUser} />
      )}

      {/* NPI Profile Tab */}
      {activeTab === 'npi' && userType === 'customer' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">NPI Profile Management</h3>
          <p className="text-gray-600 mb-6">
            Link your National Provider Identifier (NPI) to enhance your professional profile and verify your medical credentials.
          </p>
          <NPILinkingView
            currentUser={currentUser}
            onLinkingComplete={(npiLink) => {
              // Handle successful linking
              logger.debug('NPI linking completed:', npiLink);
            }}
            setCurrentView={() => {}} // No view switching needed in settings
          />
        </div>
      )}

      {/* Pre-approved Representatives Tab (for customers) */}
      {activeTab === 'representatives' && userType === 'customer' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pre-Approved Sales Representatives</h3>

            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 relative">
                <div className="flex-1 relative search-dropdown-container">
                  <input
                    type="text"
                    value={newRepEmail}
                    onChange={(e) => handleSearchReps(e.target.value)}
                    placeholder="Search by name, email, or company..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !loadingReps) {
                        addNewRep();
                      }
                    }}
                    onFocus={() => {
                      if (searchResults.length > 0) {
                        setShowSearchDropdown(true);
                      }
                    }}
                  />

                  {/* Search results dropdown */}
                  {showSearchDropdown && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {searchingReps && (
                        <div className="p-3 text-center text-gray-500">
                          <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1" />
                          Searching...
                        </div>
                      )}
                      {!searchingReps && searchResults.map((rep) => (
                        <div
                          key={rep.id}
                          onClick={() => addRepFromSearch(rep)}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{rep.name}</p>
                              <p className="text-sm text-gray-600 truncate">{rep.company}</p>
                              <p className="text-xs text-gray-500 truncate">{rep.email}</p>
                            </div>
                            <Plus className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={addNewRep}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center justify-center space-x-2 whitespace-nowrap"
                  disabled={loadingReps}
                >
                  <Plus className="w-4 h-4" />
                  <span>{loadingReps ? 'Adding...' : 'Add Rep'}</span>
                </button>
              </div>

              <div className="space-y-3">
                {approvedReps.map((rep) => (
                  <div
                    key={rep.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg gap-4"
                  >
                    <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">{rep.name}</p>
                        <p className="text-sm text-gray-600 truncate">{rep.company}</p>
                        <p className="text-xs text-gray-500 truncate">{rep.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end space-x-3 flex-shrink-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Approved:</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rep.approved}
                            onChange={() => toggleRepApproval(rep.id)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      <button
                        onClick={() => removeRep(rep.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        aria-label="Remove representative"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {approvedReps.length === 0 && !loadingReps && (
                <div className="text-center py-8">
                  <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No sales representatives added yet</p>
                  <p className="text-sm text-gray-500">Add sales rep email addresses to pre-approve them for meetings</p>
                </div>
              )}

              {loadingReps && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Pre-approved sales representatives can schedule meetings with you that will be automatically confirmed.
                All other meeting requests will require your manual approval.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Approved By Tab (for sales reps) */}
      {activeTab === 'preapproved' && (userType === 'sales' || userType === 'manager') && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Customers Who Pre-Approved You</h3>
            <p className="text-gray-600 mb-6">These customers have added you to their pre-approved list. Your meeting requests will be automatically confirmed.</p>

            <div className="space-y-3">
              {preApprovedCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-green-50 border-green-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{customer.name}</p>
                      <p className="text-sm text-gray-600">{customer.company}</p>
                      {customer.email && <p className="text-xs text-gray-500">{customer.email}</p>}
                      {customer.approvedAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          Pre-approved on {new Date(customer.approvedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    <UserCheck className="w-4 h-4" />
                    <span>Approved</span>
                  </div>
                </div>
              ))}
            </div>

            {preApprovedCustomers.length === 0 && !loadingReps && (
              <div className="text-center py-8">
                <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No customers have pre-approved you yet</p>
                <p className="text-sm text-gray-500">When customers add you to their pre-approved list, they'll appear here</p>
              </div>
            )}

            {loadingReps && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Benefit:</strong> Meetings with these customers will be automatically confirmed without requiring their manual approval, making scheduling faster and easier.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Developer Tools Tab */}
      {activeTab === 'developer' && SHOW_DEVELOPER_TOOLS && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Developer & Testing Tools</h3>
            <p className="text-sm text-gray-600 mb-6">
              These tools help with PWA testing and cache management. Only visible to admins.
            </p>

            <CacheManager />

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">How to Test PWA Updates:</h4>
              <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                <li>Make changes to the app and build/deploy</li>
                <li>On your phone, open Settings → Developer Tools</li>
                <li>Click "Clear All Caches" to remove old version</li>
                <li>App will automatically reload with latest version</li>
                <li>Test your changes on the phone</li>
              </ol>
            </div>

            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="font-semibold text-purple-900 mb-2">Service Worker Info:</h4>
              <p className="text-sm text-purple-800">
                The service worker caches app files for offline use. When you update the app,
                increment the CACHE_VERSION in service-worker.js to force cache clear for all users.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Logout Button - Always visible at bottom for mobile users */}
      {onLogout && (
        <div className="mt-6 pb-20 lg:pb-6">
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-red-200 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors font-medium"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(SettingsView);